import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from '@tanstack/react-router'
import { useForm, Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ImageUpload } from '@/components/ImageUpload'
import { FormField } from '@/components/forms/FormField'
import { FormSelect } from '@/components/forms/FormSelect'
import { FormCheckbox } from '@/components/forms/FormCheckbox'
import { MaskedFormField } from '@/components/forms/MaskedFormField'
import { useEmployee, useDepartments, useRoles } from '@/hooks/useWorkforce'
import { useUpdateEmployee } from '@/hooks/useWorkforceMutations'
import {
  employeeSchema,
  EmployeeFormData,
  formatCPF,
  formatPhone,
  formatCEP,
  BRAZILIAN_STATES,
} from '@/lib/validations/workforce'
import {
  ArrowLeft,
  Save,
  User,
  Briefcase,
  MapPin,
  Phone,
  Shield,
} from 'lucide-react'
import { toast } from 'sonner'

function EditEmployeeContent() {
  const navigate = useNavigate()
  const { employeeId } = useParams({ from: '/_auth/workforce/employees/$employeeId/edit' })

  // Local state for image upload
  const [pictureKey, setPictureKey] = useState<string>('')
  const [pictureUrl, setPictureUrl] = useState<string>('')

  // Server state
  const { employee, loading: employeeLoading } = useEmployee(employeeId)
  const { departments, loading: departmentsLoading } = useDepartments()
  const { roles, loading: rolesLoading } = useRoles()

  // Mutations
  const updateEmployeeMutation = useUpdateEmployee()

  const loading = employeeLoading || departmentsLoading || rolesLoading

  // ✨ React Hook Form with Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    control,
    reset,
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema) as any,
    mode: 'onBlur', // Validate on blur to show errors immediately
  })

  const selectedDepartmentId = watch('departmentId')

  // Populate form when employee data loads
  useEffect(() => {
    if (!employee) return

    const nameParts = employee.personalInfo?.name?.split(' ') || []
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    const formData = {
      firstName,
      lastName,
      email: employee.email || '',
      phone: employee.personalInfo?.phone || '',
      cpf: employee.personalInfo?.document || '',
      departmentId: employee.currentEngagement?.departmentId?.toString() || '',
      roleId: employee.currentEngagement?.roleId?.toString() || '',
      hireDate: employee.currentEngagement?.startDate
        ? new Date(employee.currentEngagement.startDate)
        : new Date(),
      salary: employee.currentEngagement?.salary || 0,
      address: {
        street: employee.personalInfo?.address?.street || '',
        city: employee.personalInfo?.address?.city || '',
        state: employee.personalInfo?.address?.state || '',
        zipCode: employee.personalInfo?.address?.zipCode || '',
      },
      emergencyContact: {
        name: employee.personalInfo?.emergencyContact?.name || '',
        relationship: '',
        phone: employee.personalInfo?.emergencyContact?.phone || '',
      },
      benefits: {
        healthInsurance: employee.benefits?.healthInsurance || false,
        dentalInsurance: employee.benefits?.dentalInsurance || false,
        visionInsurance: false,
        lifeInsurance: employee.benefits?.lifeInsurance || false,
        retirement401k: employee.benefits?.retirement401k || false,
        paidTimeOff: employee.benefits?.paidTimeOff || 20,
      },
    }

    console.log('>>> EDIT PAGE - Form reset data:', formData)
    reset(formData)

    // Set picture data if available
    if (employee.personalInfo?.pictureKey) {
      setPictureKey(employee.personalInfo.pictureKey)
    }
    if (employee.personalInfo?.pictureUrl) {
      setPictureUrl(employee.personalInfo.pictureUrl)
    }
  }, [employee, reset])

  const submitting = isSubmitting || updateEmployeeMutation.isPending

  // Form submission
  const onSubmit = async (data: EmployeeFormData) => {
    if (!employeeId) return

    try {
      const updates = {
        email: data.email,
        personalInfo: {
          name: `${data.firstName} ${data.lastName}`,
          phone: data.phone,
          document: data.cpf,
          birthDate: employee?.personalInfo?.birthDate || null,
          pictureKey: pictureKey || undefined,
          pictureUrl: pictureUrl || undefined,
          address: data.address,
          emergencyContact: {
            name: data.emergencyContact?.name || '',
            phone: data.emergencyContact?.phone || '',
          },
        },
        employment: {
          departmentId: data.departmentId,
          roleId: data.roleId,
          startDate: data.hireDate.toISOString(),
          status: employee?.status || 'ACTIVE',
          salary: data.salary,
          contractType: employee?.currentEngagement?.contractType || 'CLT',
        },
        benefits: {
          healthInsurance: data.benefits?.healthInsurance || false,
          dentalInsurance: data.benefits?.dentalInsurance || false,
          lifeInsurance: data.benefits?.lifeInsurance || false,
          mealVoucher: 0,
          transportVoucher: 0,
          gymMembership: false,
          retirement401k: data.benefits?.retirement401k || false,
          paidTimeOff: data.benefits?.paidTimeOff || 20,
        },
      }

      await updateEmployeeMutation.mutateAsync({
        employeeId,
        updates: updates as any,
      })

      toast.success('Colaborador atualizado com sucesso!')
      navigate({ to: `/workforce/employees/${employeeId}` })
    } catch (error) {
      console.error('Error updating employee:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erro ao atualizar colaborador'
      toast.error(errorMessage)
    }
  }

  // Debug: Log departments and roles
  console.log('>>> EDIT PAGE - Departments:', departments.length, departments)
  console.log('>>> EDIT PAGE - Roles:', roles.length, roles)
  console.log('>>> EDIT PAGE - Employee currentEngagement:', employee?.currentEngagement)

  const departmentOptions = departments.map((dept) => ({
    value: dept.departmentId,
    label: dept.name,
  }))

  const roleOptions = roles.map((role) => ({
    value: role.roleId,
    label: role.name,
  }))

  console.log('>>> EDIT PAGE - Department options:', departmentOptions)
  console.log('>>> EDIT PAGE - Role options:', roleOptions)

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-brand-gray">Colaborador não encontrado</p>
        <Link to={"/workforce/employees" as any}>
          <Button variant="ghost" className="mt-4">
            Voltar para lista
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to={`/workforce/employees/${employeeId}` as any}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <div>
            <h1 className="font-display text-3xl font-bold text-white">Editar Colaborador</h1>
            <p className="text-brand-gray">Atualize as informações de {employee.personalInfo.name}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-6">
        {/* Basic Information */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">
              Informações Básicas
            </h2>
          </div>

          {/* Picture Upload */}
          <div className="mb-6">
            <ImageUpload
              employeeId={employeeId}
              currentImageUrl={pictureUrl}
              onUploadComplete={(key, url) => {
                setPictureKey(key)
                setPictureUrl(url)
              }}
              onError={(error) => console.error('Image upload error:', error)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Nome"
              name="firstName"
              register={register}
              error={errors.firstName}
              required
              placeholder="Digite o nome"
            />

            <FormField
              label="Sobrenome"
              name="lastName"
              register={register}
              error={errors.lastName}
              required
              placeholder="Digite o sobrenome"
            />

            <FormField
              label="Email"
              name="email"
              type="email"
              register={register}
              error={errors.email}
              required
              placeholder="email@exemplo.com"
            />

            <MaskedFormField
              label="Telefone"
              name="phone"
              control={control as any}
              error={errors.phone}
              placeholder="(11) 99999-9999"
              maskFunction={formatPhone}
            />

            <MaskedFormField
              label="CPF"
              name="cpf"
              control={control as any}
              error={errors.cpf}
              placeholder="000.000.000-00"
              maskFunction={formatCPF}
            />

            <FormField
              label="Data de Admissão"
              name="hireDate"
              type="date"
              register={register}
              error={errors.hireDate}
              required
            />
          </div>
        </Card>

        {/* Employment Information */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Briefcase className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">
              Informações de Emprego
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FormSelect
              label="Departamento"
              name="departmentId"
              register={register}
              error={errors.departmentId}
              required
              options={departmentOptions}
              placeholder="Selecione um departamento"
            />

            <FormSelect
              label="Cargo"
              name="roleId"
              register={register}
              error={errors.roleId}
              required
              options={roleOptions}
              placeholder="Selecione um cargo"
            />

            <FormField
              label="Salário (R$)"
              name="salary"
              type="number"
              register={register}
              error={errors.salary}
              placeholder="0.00"
              min={0}
              step={0.01}
            />
          </div>
        </Card>

        {/* Address */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">Endereço</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <FormField
                label="Rua"
                name="address.street"
                register={register}
                error={errors.address?.street}
                placeholder="Rua, número, complemento"
              />
            </div>

            <FormField
              label="Cidade"
              name="address.city"
              register={register}
              error={errors.address?.city}
              placeholder="Cidade"
            />

            <FormSelect
              label="Estado"
              name="address.state"
              register={register}
              error={errors.address?.state}
              options={BRAZILIAN_STATES}
              placeholder="Selecione o estado"
            />

            <MaskedFormField
              label="CEP"
              name="address.zipCode"
              control={control as any}
              error={errors.address?.zipCode}
              placeholder="00000-000"
              maskFunction={formatCEP}
            />
          </div>
        </Card>

        {/* Benefits */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">Benefícios</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <FormCheckbox
              label="Plano de Saúde"
              name="benefits.healthInsurance"
              register={register}
            />

            <FormCheckbox
              label="Plano Dental"
              name="benefits.dentalInsurance"
              register={register}
            />

            <FormCheckbox
              label="Plano de Visão"
              name="benefits.visionInsurance"
              register={register}
            />

            <FormCheckbox
              label="Seguro de Vida"
              name="benefits.lifeInsurance"
              register={register}
            />

            <FormCheckbox
              label="Previdência"
              name="benefits.retirement401k"
              register={register}
            />

            <FormField
              label="Dias de Férias"
              name="benefits.paidTimeOff"
              type="number"
              register={register}
              error={errors.benefits?.paidTimeOff}
              min={0}
              max={365}
            />
          </div>
        </Card>

        {/* Emergency Contact */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Phone className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">
              Contato de Emergência
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Nome"
              name="emergencyContact.name"
              register={register}
              error={errors.emergencyContact?.name}
              placeholder="Nome do contato"
            />

            <FormField
              label="Parentesco"
              name="emergencyContact.relationship"
              register={register}
              error={errors.emergencyContact?.relationship}
              placeholder="Ex: Mãe, Pai, Cônjuge"
            />

            <MaskedFormField
              label="Telefone"
              name="emergencyContact.phone"
              control={control as any}
              error={errors.emergencyContact?.phone}
              placeholder="(11) 99999-9999"
              maskFunction={formatPhone}
            />
          </div>
        </Card>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <Link to={`/workforce/employees/${employeeId}` as any}>
            <Button variant="ghost" disabled={submitting}>
              Cancelar
            </Button>
          </Link>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default withPageProtection(EditEmployeeContent, PAGES.WORKFORCE_EMPLOYEES_EDIT)
